import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "./CurrencyMasterPage";
import { Field } from "./BankAccountsPage";
import { SegBadge } from "./OpeningBalancesPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { useAppCtx } from "../contexts/AppContext";
import { PartnerAutocomplete } from "../components/PartnerAutocomplete";
import { PeriodFilter, periodParams } from "../components/PeriodFilter";
import { Trash2, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";

const LINE_TYPES = [
    { value: "bank", label: "Bank account" },
    { value: "partner", label: "Partner" },
    { value: "overhead", label: "Overhead ledger" },
    { value: "general", label: "General ledger" },
];

function num(n) {
    return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function JournalsPage() {
    const { hasPerm } = useAuth();
    const canWrite = hasPerm("journals", "write");
    const { selectedSegment } = useAppCtx();
    const [period, setPeriod] = useState({ from: null, to: null, preset: "this_month" });
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(blank());

    const [banks, setBanks] = useState([]);
    const [overheads, setOverheads] = useState([]);
    const [generals, setGenerals] = useState([]);

    function blank() {
        return {
            segment: "Voice",
            date: new Date().toISOString().slice(0, 10),
            voucher_number: "",
            narration: "",
            lines: [emptyLine("USD"), emptyLine("USD")],
        };
    }
    function emptyLine(cur) {
        return { line_type: "bank", target_id: "", target_name: "", currency: cur, dr: "", cr: "", description: "", fx_rate: "" };
    }

    const load = async () => {
        const params = { ...periodParams(period) };
        if (selectedSegment !== "all") params.segment = selectedSegment;
        const { data } = await api.get("/journals", { params });
        setRows(data || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedSegment, period]);

    useEffect(() => {
        Promise.all([
            api.get("/banks"), api.get("/overhead-ledgers"), api.get("/general-ledgers"),
        ]).then(([b, o, g]) => {
            setBanks(b.data || []);
            setOverheads(o.data || []);
            setGenerals((g.data || []).filter((x) => x.active !== false));
        }).catch(() => {});
    }, []);

    // Auto-suggest voucher number on date/segment change for new vouchers
    useEffect(() => {
        if (editId || form.voucher_number) return;
        api.get("/journals/next-number", { params: { date: form.date } })
            .then(({ data }) => setForm((f) => ({ ...f, voucher_number: data.voucher_number })))
            .catch(() => {});
    }, [form.date, editId, form.voucher_number]);

    const startNew = () => {
        const baseCur = "USD";
        const f = blank();
        f.lines = [emptyLine(baseCur), emptyLine(baseCur)];
        setForm(f);
        setEditId(null);
        setOpen(true);
    };
    const startEdit = (r) => {
        setEditId(r.id);
        setForm({
            segment: r.segment, date: r.date,
            voucher_number: r.voucher_number,
            narration: r.narration || "",
            lines: (r.lines || []).map((l) => ({
                line_type: l.line_type,
                target_id: l.target_id || "",
                target_name: l.target_name || "",
                currency: l.currency,
                dr: l.dr || "",
                cr: l.cr || "",
                description: l.description || "",
                fx_rate: l.fx_rate || "",
            })),
        });
        setOpen(true);
    };
    const close = () => { setOpen(false); setEditId(null); setForm(blank()); };

    const setLine = (idx, patch) => {
        const lines = form.lines.slice();
        lines[idx] = { ...lines[idx], ...patch };
        setForm({ ...form, lines });
    };
    const addLine = () => setForm({ ...form, lines: [...form.lines, emptyLine(form.segment === "Voice" ? "USD" : "EUR")] });
    const removeLine = (idx) => {
        if (form.lines.length <= 2) return;
        setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) });
    };

    const submit = async () => {
        if (!form.lines || form.lines.length < 2) return toast.error("At least 2 lines required");
        const body = {
            segment: form.segment,
            date: form.date,
            voucher_number: form.voucher_number || null,
            narration: form.narration,
            lines: form.lines.map((l) => ({
                line_type: l.line_type,
                target_id: l.target_id || null,
                target_name: l.target_name || null,
                currency: (l.currency || "").toUpperCase(),
                dr: Number(l.dr || 0),
                cr: Number(l.cr || 0),
                description: l.description,
                fx_rate: l.fx_rate ? Number(l.fx_rate) : null,
            })),
        };
        try {
            if (editId) await api.put(`/journals/${editId}`, body);
            else await api.post("/journals", body);
            toast.success(editId ? "Voucher updated" : "Voucher created");
            close();
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this voucher?")) return;
        try { await api.delete(`/journals/${id}`); await load(); } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    };

    const drTotal = form.lines.reduce((s, l) => s + Number(l.dr || 0), 0);
    const crTotal = form.lines.reduce((s, l) => s + Number(l.cr || 0), 0);

    return (
        <div data-testid="journals-page">
            <PageHeader
                title="Journal Vouchers"
                eyebrow="General Journal"
                description="Multi-line Dr/Cr entries for everything outside Sales / Purchase / Credit Notes / Receipts / Payments."
                action={canWrite ? <Button className="rounded-sm bg-[#002FA7] hover:bg-[#001f6f]" onClick={startNew} data-testid="jv-add-btn">+ New voucher</Button> : null}
            />
            <div className="flex flex-wrap items-center gap-3 mb-3">
                <PeriodFilter initialPreset="this_month" onChange={setPeriod}/>
                <span className="text-[11px] text-slate-500 ml-auto">{rows.length} voucher(s)</span>
            </div>
            <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="jv-table">
                <table className="w-full text-[13px] financial min-w-[1000px]">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                            <th className="px-3 py-2.5">Date</th>
                            <th className="px-3 py-2.5">Voucher #</th>
                            <th className="px-3 py-2.5">Seg</th>
                            <th className="px-3 py-2.5">Narration</th>
                            <th className="px-3 py-2.5">Lines</th>
                            <th className="px-3 py-2.5 num">Dr (Base)</th>
                            <th className="px-3 py-2.5 num">Cr (Base)</th>
                            <th className="px-3 py-2.5 w-24"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && <tr><td colSpan={8} className="text-center text-slate-500 py-10">No vouchers in this period</td></tr>}
                        {rows.map((r) => (
                            <tr key={r.id} className="border-b border-slate-100" data-testid={`jv-row-${r.id}`}>
                                <td className="px-3 py-2 font-mono">{r.date}</td>
                                <td className="px-3 py-2 font-mono text-[#002FA7]">{r.voucher_number}</td>
                                <td className="px-3 py-2"><SegBadge segment={r.segment}/></td>
                                <td className="px-3 py-2 truncate max-w-[300px]">{r.narration || "—"}</td>
                                <td className="px-3 py-2 text-slate-500">{(r.lines || []).length}</td>
                                <td className="px-3 py-2 num font-semibold">{num(r.total_dr_base)}</td>
                                <td className="px-3 py-2 num font-semibold">{num(r.total_cr_base)}</td>
                                <td className="px-3 py-2">
                                    {canWrite && (
                                        <div className="flex gap-1">
                                            <Button variant="ghost" size="sm" onClick={() => startEdit(r)} className="h-7 px-2" data-testid={`jv-edit-${r.id}`}><Pencil size={13}/></Button>
                                            <Button variant="ghost" size="sm" onClick={() => remove(r.id)} className="h-7 px-2 text-red-600" data-testid={`jv-delete-${r.id}`}><Trash2 size={13}/></Button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={open} onOpenChange={(v) => v ? setOpen(true) : close()}>
                <DialogContent className="rounded-sm max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="jv-dialog">
                    <DialogHeader><DialogTitle>{editId ? "Edit voucher" : "New journal voucher"}</DialogTitle></DialogHeader>
                    <div className="grid grid-cols-3 gap-3 mb-3">
                        <Field label="Segment *">
                            <Select value={form.segment} onValueChange={(v) => setForm({ ...form, segment: v })}>
                                <SelectTrigger className="rounded-sm" data-testid="jv-segment"><SelectValue/></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Voice">Voice (USD base)</SelectItem>
                                    <SelectItem value="SMS">SMS (EUR base)</SelectItem>
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Date *">
                            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm h-9 font-mono" data-testid="jv-date"/>
                        </Field>
                        <Field label="Voucher # (auto)">
                            <Input value={form.voucher_number} onChange={(e) => setForm({ ...form, voucher_number: e.target.value })} className="rounded-sm h-9 font-mono" data-testid="jv-vno"/>
                        </Field>
                        <div className="col-span-3">
                            <Field label="Narration">
                                <Textarea rows={2} value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })} className="rounded-sm" data-testid="jv-narration"/>
                            </Field>
                        </div>
                    </div>

                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Lines</div>
                    <div className="border border-slate-200 rounded-sm">
                        <div className="grid grid-cols-12 gap-2 px-2 py-1.5 bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                            <div className="col-span-2">Type</div>
                            <div className="col-span-3">Target</div>
                            <div className="col-span-2">Description</div>
                            <div className="col-span-1">Cur</div>
                            <div className="col-span-1 num">Dr</div>
                            <div className="col-span-1 num">Cr</div>
                            <div className="col-span-1 num">FX</div>
                            <div className="col-span-1"></div>
                        </div>
                        {form.lines.map((l, i) => (
                            <LineRow key={i} idx={i} line={l} setLine={setLine} removeLine={removeLine}
                                     banks={banks} overheads={overheads} generals={generals}/>
                        ))}
                        <div className="px-2 py-1.5 border-t border-slate-200 flex items-center justify-between bg-slate-50">
                            <Button size="sm" variant="ghost" onClick={addLine} data-testid="jv-add-line"><Plus size={12} className="mr-1"/>Add line</Button>
                            <div className="flex gap-6 text-[12px] font-mono">
                                <span>Dr: <strong>{num(drTotal)}</strong></span>
                                <span>Cr: <strong>{num(crTotal)}</strong></span>
                                <span className={Math.abs(drTotal - crTotal) > 0.01 ? "text-red-600 font-semibold" : "text-emerald-700 font-semibold"}>
                                    Diff: {num(Math.abs(drTotal - crTotal))}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-2">
                        Note: Voucher must balance in the segment base currency ({form.segment === "Voice" ? "USD" : "EUR"}). FX is applied per line; same-currency lines must sum to zero in base.
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={close}>Cancel</Button>
                        <Button onClick={submit} className="rounded-sm bg-[#002FA7]" data-testid="jv-save-btn">{editId ? "Update voucher" : "Create voucher"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function LineRow({ idx, line, setLine, removeLine, banks, overheads, generals }) {
    const setType = (t) => setLine(idx, { line_type: t, target_id: "", target_name: "" });
    return (
        <div className="grid grid-cols-12 gap-2 px-2 py-1.5 border-b border-slate-100 items-center" data-testid={`jv-line-${idx}`}>
            <div className="col-span-2">
                <Select value={line.line_type} onValueChange={setType}>
                    <SelectTrigger className="h-8 rounded-sm text-[12px]" data-testid={`jv-line-${idx}-type`}><SelectValue/></SelectTrigger>
                    <SelectContent>{LINE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
            </div>
            <div className="col-span-3">
                {line.line_type === "partner" ? (
                    <PartnerAutocomplete
                        value={line.target_name}
                        onChange={(v) => setLine(idx, { target_name: v })}
                        placeholder="Type partner name…"
                        testId={`jv-line-${idx}-partner`}
                    />
                ) : (
                    <Select value={line.target_id} onValueChange={(v) => {
                        const list = line.line_type === "bank" ? banks : line.line_type === "overhead" ? overheads : generals;
                        const tgt = list.find((x) => x.id === v);
                        setLine(idx, { target_id: v, target_name: tgt?.name || "" });
                    }}>
                        <SelectTrigger className="h-8 rounded-sm text-[12px]" data-testid={`jv-line-${idx}-target`}>
                            <SelectValue placeholder={line.line_type === "bank" ? "Bank…" : line.line_type === "overhead" ? "Overhead ledger…" : "General ledger…"}/>
                        </SelectTrigger>
                        <SelectContent>
                            {(line.line_type === "bank" ? banks : line.line_type === "overhead" ? overheads : generals).map((x) => (
                                <SelectItem key={x.id} value={x.id}>
                                    {x.name}{x.currency ? ` (${x.currency})` : ""}{x.nature ? ` — ${x.nature}` : ""}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            </div>
            <div className="col-span-2">
                <Input value={line.description} onChange={(e) => setLine(idx, { description: e.target.value })} className="h-8 rounded-sm text-[12px]" data-testid={`jv-line-${idx}-desc`}/>
            </div>
            <div className="col-span-1">
                <Input value={line.currency} onChange={(e) => setLine(idx, { currency: e.target.value.toUpperCase() })} className="h-8 rounded-sm text-[12px] font-mono" data-testid={`jv-line-${idx}-cur`}/>
            </div>
            <div className="col-span-1">
                <Input type="number" step="0.01" value={line.dr} onChange={(e) => setLine(idx, { dr: e.target.value, cr: e.target.value ? "" : line.cr })} className="h-8 rounded-sm text-[12px] font-mono num" data-testid={`jv-line-${idx}-dr`}/>
            </div>
            <div className="col-span-1">
                <Input type="number" step="0.01" value={line.cr} onChange={(e) => setLine(idx, { cr: e.target.value, dr: e.target.value ? "" : line.dr })} className="h-8 rounded-sm text-[12px] font-mono num" data-testid={`jv-line-${idx}-cr`}/>
            </div>
            <div className="col-span-1">
                <Input type="number" step="0.000001" value={line.fx_rate} onChange={(e) => setLine(idx, { fx_rate: e.target.value })} placeholder="auto" className="h-8 rounded-sm text-[12px] font-mono num" data-testid={`jv-line-${idx}-fx`}/>
            </div>
            <div className="col-span-1 text-right">
                <Button variant="ghost" size="sm" onClick={() => removeLine(idx)} className="h-7 px-2 text-red-500" data-testid={`jv-line-${idx}-del`}><Trash2 size={12}/></Button>
            </div>
        </div>
    );
}
